import { useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import debounce from 'lodash/debounce';
import { useWindowSize } from 'usehooks-ts';

import FilterOption from '@publicImages/svg/filters.svg?static';

import UIResource from '@/components/UIResource';
import getFocusableElements from '@/ducks/a11y/helpers/getFocusableElements';
import { setFiltersModalOpen } from '@/ducks/pages/chooseVoyage/slice';
import { useAppDispatch } from '@/store';
import tagmanager from '@/tagmanager';

import styles from './SearchAgainButton.module.scss';

const filtersRowId = 'ResultRefinements__newFilterPrimaryRow';

const SearchAgainButton = () => {
  const dispatch = useAppDispatch();
  const { width } = useWindowSize();

  const onSearch = () => {
    tagmanager.tracker.voyagesFilter.trackReApplyFilters();

    if (width < 768) {
      dispatch(setFiltersModalOpen(true));
      return;
    }

    const target = document.getElementById(filtersRowId);
    if (!target) return;

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const focusable = getFocusableElements(target);
        focusable[0]?.focus();
      });
    });
  };

  return (
    <button className={styles.button} onClick={onSearch}>
      <FilterOption className={styles.icon} />
      <span className={styles.text}>
        <UIResource id="ChooseVoyage.SearchAgain.button" />
      </span>
    </button>
  );
};

export const SearchAgainButtonContainer = ({ children, className, ...props }: React.ComponentProps<'div'>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { width } = useWindowSize();

  const triggerRatio = useMemo(() => {
    if (width >= 2560) return 0.25;
    if (width >= 768) return 0.5;
    return 0.7;
  }, [width]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const triggerPoint = window.innerHeight * triggerRatio;
      setIsVisible(scrollY >= triggerPoint);
    };

    const debouncedHandleScroll = debounce(handleScroll, 100);
    debouncedHandleScroll(); // initial check

    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      debouncedHandleScroll.cancel(); // clean up debounce
    };
  }, [triggerRatio]);

  return (
    <div className={cn(isVisible && styles.show, className)} ref={ref} {...props}>
      {children}
      <SearchAgainButton />
    </div>
  );
};
